import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { relTime, formatCurrency } from "@/lib/api";
import PageHeader from "@/components/layout/PageHeader";
import { LEADS } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { CheckCircle2, XCircle, RotateCcw, ArrowRight, Tag, Search } from "lucide-react";
import ChannelIcon, { CHANNEL_COLORS } from "../components/ChannelIcon";
import ContactAvatar from "../components/ContactAvatar";
import DateRangeFilter from "../components/DateRangeFilter";

const DEFAULT_TAGS = ["Malay", "Indian", "Chinese", "Decor", "Enterprise", "SMB", "Referral"];

export default function Leads() {
  const [tab, setTab] = useState("queue");
  const [selected, setSelected] = useState(null);
  const [showConvert, setShowConvert] = useState(false);
  const [showDisqualify, setShowDisqualify] = useState(false);
  const [search, setSearch] = useState("");
  const [range, setRange] = useState({ from: null, to: null, preset: null });

  const { data: leads = [], isLoading } = useQuery({
    queryKey: ["leads", tab, search, range.from, range.to],
    queryFn: () => api.get(`/leads`, { params: { status: tab, q: search || undefined, date_from: range.from || undefined, date_to: range.to || undefined } }).then((r) => r.data),
    refetchInterval: 10000,
  });

  return (
    <div>
      <PageHeader
        overline="Qualification"
        title="Leads Queue"
        subtitle="Two-stage process. Qualify here, then push to pipeline."
      />

      <div className="px-6 lg:px-10 pb-10">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="rounded-sm bg-secondary/40">
            <TabsTrigger value="queue" className="rounded-sm">Queue</TabsTrigger>
            <TabsTrigger value="converted" className="rounded-sm">Converted</TabsTrigger>
            <TabsTrigger value="disqualified" className="rounded-sm">Disqualified</TabsTrigger>
          </TabsList>

          <div className="flex items-center gap-3 flex-wrap mt-4">
            <div className="relative flex-1 min-w-[220px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input data-testid="leads-search" placeholder="Search lead name, email, phone, notes…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 rounded-sm bg-secondary/40" />
            </div>
            <DateRangeFilter value={range} onChange={setRange} testId="leads-date" />
          </div>

          <TabsContent value={tab} className="mt-6">
            {isLoading && <div className="text-sm text-muted-foreground">Loading…</div>}
            {!isLoading && leads.length === 0 && (
              <Card className="rounded-sm bg-card border-border p-12 text-center">
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold">Empty</div>
                <div className="font-display text-2xl font-extrabold mt-2">No leads in this bucket.</div>
                <div className="text-sm text-muted-foreground mt-2">Tag any Inbox conversation as “Lead” to send it here.</div>
              </Card>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {leads.map((l) => {
                const brand = CHANNEL_COLORS[l.source_channel] || CHANNEL_COLORS.chat;
                return (
                <Card key={l.id} data-testid={LEADS.card} className="rounded-sm bg-card border-border p-5 group relative overflow-hidden card-in">
                  <div className="absolute top-0 left-0 h-full w-1" style={{ backgroundColor: brand.color, opacity: 0.7 }} />
                  <div className="flex items-start justify-between mb-3 pl-2">
                    <div className="flex items-start gap-3 min-w-0">
                      <div className="relative flex-none">
                        <ContactAvatar src={l.contact?.avatar_url} name={l.contact?.name} size={36} />
                        <span className="absolute -bottom-1 -right-1">
                          <ChannelIcon channel={l.source_channel} size={9} badge />
                        </span>
                      </div>
                      <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-[0.2em] font-bold flex items-center gap-1.5" style={{ color: brand.color }}>
                          Lead · <span className="capitalize">{l.source_channel}</span>
                        </div>
                        <div className="font-display text-lg font-bold mt-0.5 truncate">{l.contact?.name}</div>
                        <div className="text-xs text-muted-foreground truncate">{l.contact?.email || l.contact?.phone}</div>
                      </div>
                    </div>
                    <Badge variant="outline" className="rounded-sm text-[10px] flex-none">{relTime(l.created_at)}</Badge>
                  </div>
                  {l.contact?.company_name && <div className="text-xs pl-2 text-muted-foreground mb-3">{l.contact.company_name}</div>}
                  {l.assigned_agent && <div className="text-xs pl-2 text-muted-foreground mb-3">Assigned: <span className="text-white">{l.assigned_agent.name}</span></div>}
                  {l.disqualify_reason && <div className="text-xs pl-2 text-red-400 mb-3">Reason: {l.disqualify_reason}</div>}

                  <div className="flex gap-2 pl-2 mt-2">
                    {tab === "queue" && (
                      <>
                        <Button data-testid={LEADS.convertButton} onClick={() => { setSelected(l); setShowConvert(true); }} className="rounded-sm bg-primary hover:bg-primary/90 flex-1"><ArrowRight className="h-3.5 w-3.5 mr-1" />Convert</Button>
                        <Button data-testid={LEADS.disqualifyButton} onClick={() => { setSelected(l); setShowDisqualify(true); }} variant="outline" className="rounded-sm"><XCircle className="h-3.5 w-3.5" /></Button>
                      </>
                    )}
                    {tab === "disqualified" && <ReactivateBtn leadId={l.id} />}
                    {tab === "converted" && (
                      <Badge className="rounded-sm bg-green-500/20 text-green-400 border-green-500/30"><CheckCircle2 className="h-3 w-3 mr-1" />Deal Created</Badge>
                    )}
                  </div>
                </Card>
                );
              })}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {selected && <ConvertDialog open={showConvert} lead={selected} onClose={() => setShowConvert(false)} />}
      {selected && <DisqualifyDialog open={showDisqualify} lead={selected} onClose={() => setShowDisqualify(false)} />}
    </div>
  );
}

function ReactivateBtn({ leadId }) {
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: () => api.post(`/leads/${leadId}/reactivate`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["leads"] }); toast.success("Reactivated"); },
  });
  return <Button data-testid={LEADS.reactivateButton} onClick={() => mut.mutate()} className="rounded-sm bg-primary/20 text-white hover:bg-primary/30 flex-1"><RotateCcw className="h-3.5 w-3.5 mr-1" />Reactivate</Button>;
}

function ConvertDialog({ open, lead, onClose }) {
  const [dealName, setDealName] = useState(lead.contact?.name ? `${lead.contact.name} - New Deal` : "New Deal");
  const [value, setValue] = useState(1000);
  const [stageId, setStageId] = useState("");
  const [tags, setTags] = useState([]);
  const [expectedClose, setExpectedClose] = useState("");
  const qc = useQueryClient();

  const { data: stages = [] } = useQuery({ queryKey: ["stages"], queryFn: () => api.get("/pipeline/stages").then((r) => r.data) });
  React.useEffect(() => { if (!stageId && stages[0]) setStageId(stages[0].id); }, [stages, stageId]);

  const mut = useMutation({
    mutationFn: () => api.post(`/leads/${lead.id}/convert`, { deal_name: dealName, value: Number(value), stage_id: stageId, tags, expected_close_date: expectedClose || null }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["leads"] }); qc.invalidateQueries({ queryKey: ["deals"] }); toast.success("Lead converted to deal"); onClose(); },
    onError: (e) => toast.error(e.response?.data?.detail || "Failed"),
  });

  const toggleTag = (t) => setTags((prev) => prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="rounded-sm max-w-lg">
        <DialogHeader><DialogTitle className="font-display text-2xl font-extrabold tracking-tight">Convert to Deal</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Deal name</Label><Input className="rounded-sm mt-1.5 bg-secondary/40" value={dealName} onChange={(e) => setDealName(e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Expected value</Label><Input type="number" className="rounded-sm mt-1.5 bg-secondary/40" value={value} onChange={(e) => setValue(e.target.value)} /></div>
            <div><Label>Expected close</Label><Input type="date" className="rounded-sm mt-1.5 bg-secondary/40" value={expectedClose} onChange={(e) => setExpectedClose(e.target.value)} /></div>
          </div>
          <div>
            <Label>Initial stage</Label>
            <Select value={stageId} onValueChange={setStageId}>
              <SelectTrigger className="rounded-sm mt-1.5 bg-secondary/40"><SelectValue /></SelectTrigger>
              <SelectContent>{stages.filter((s) => !s.is_won && !s.is_lost).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="flex items-center gap-1.5"><Tag className="h-3 w-3" />Tags / Categories</Label>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {DEFAULT_TAGS.map((t) => (
                <button key={t} onClick={() => toggleTag(t)} className={`text-xs px-2.5 py-1 rounded-sm border transition-colors ${tags.includes(t) ? "bg-primary text-white border-primary" : "border-border text-muted-foreground hover:bg-secondary/60"}`}>{t}</button>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
          <Button data-testid={LEADS.convertSubmit} disabled={mut.isPending || !stageId} onClick={() => mut.mutate()} className="rounded-sm bg-primary hover:bg-primary/90">Convert to Deal →</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DisqualifyDialog({ open, lead, onClose }) {
  const [reason, setReason] = useState("Not a fit");
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: () => api.post(`/leads/${lead.id}/disqualify`, { reason }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["leads"] }); toast.success("Marked as disqualified"); onClose(); },
  });
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="rounded-sm max-w-lg">
        <DialogHeader><DialogTitle className="font-display text-2xl font-extrabold tracking-tight">Disqualify Lead</DialogTitle></DialogHeader>
        <div><Label>Reason</Label><Textarea rows={3} className="rounded-sm mt-1.5 bg-secondary/40" value={reason} onChange={(e) => setReason(e.target.value)} /></div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
          <Button data-testid={LEADS.disqualifySubmit} onClick={() => mut.mutate()} disabled={mut.isPending} className="rounded-sm bg-red-600 hover:bg-red-700 text-white">Disqualify</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
